import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FormSheet, OfficialUseBlock, SectionBand } from '../components/FormSheet'
import {
  EMAIL_LIMIT,
  fileComplaint,
  GRIEVANCE_LIMIT,
  NAME_LIMIT,
  RESPONDENT_LIMIT,
} from '../lib/complaints'
import { getIntake, type Intake } from '../lib/intake'

export function FilePage() {
  const history = useHistory()
  const [filedBy, setFiledBy] = useState('')
  const [complainantEmail, setComplainantEmail] = useState('')
  const [respondent, setRespondent] = useState('')
  const [complaintText, setComplaintText] = useState('')
  const [notice, setNotice] = useState('')
  const [filing, setFiling] = useState(false)
  const [intake, setIntake] = useState<Intake | null>(null)

  // Whether the window is open decides which page this is, so it is settled
  // before anything is put in front of a complainant.
  useEffect(() => {
    let current = true
    getIntake()
      .then((state) => current && setIntake(state))
      .catch(() => {
        // The department could not be reached. Show the form rather than a
        // closure that may not exist -- filing is refused server-side anyway.
        if (current) setIntake({ accepting: true, reopenEstimate: null, updatedAt: null })
      })
    return () => {
      current = false
    }
  }, [])

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (filing) return

    if (!filedBy.trim()) {
      setNotice('Enter the name of the complainant.')
      return
    }
    if (!respondent.trim()) {
      setNotice('Name the respondent. If no person is responsible, name the thing that is.')
      return
    }
    if (!complaintText.trim()) {
      setNotice('The statement of grievance cannot be left blank.')
      return
    }

    setNotice('')
    setFiling(true)
    try {
      const filed = await fileComplaint(
        filedBy.trim(),
        respondent.trim(),
        complaintText.trim(),
        complainantEmail,
      )
      history.push(`/complaint/${filed.caseNumber}`)
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'The form could not be filed.')
      setFiling(false)
    }
  }

  if (!intake) {
    return (
      <FormSheet>
        <div className="px-4 py-16 text-center font-form text-[11px] uppercase tracking-[0.22em] text-carbon/50 sm:px-7">
          Consulting the department…
        </div>
      </FormSheet>
    )
  }

  if (!intake.accepting) {
    return <WindowClosed intake={intake} />
  }

  return (
    <FormSheet>
      <form onSubmit={onSubmit} noValidate>
        <SectionBand number={1} title="Complainant" />
        <div className="px-4 pb-6 pt-5 sm:px-7">
          <FieldLabel
            htmlFor="filedBy"
            number="1"
            label="Name of complainant"
            hint="Print clearly. Nicknames are acceptable."
          />
          <input
            id="filedBy"
            type="text"
            value={filedBy}
            maxLength={NAME_LIMIT}
            autoComplete="off"
            onChange={(e) => setFiledBy(e.target.value)}
            className="mt-2 w-full border-b border-carbon/50 bg-transparent px-1 pb-1 font-type text-[17px] text-carbon outline-none focus:border-process"
          />

          <div className="mt-5">
            <FieldLabel
              htmlFor="complainantEmail"
              number="2"
              label="Email address (optional)"
              hint="Where the department will send its ruling. Leave blank and you may read the ruling on your receipt instead."
            />
            <input
              id="complainantEmail"
              type="email"
              value={complainantEmail}
              maxLength={EMAIL_LIMIT}
              autoComplete="email"
              onChange={(e) => setComplainantEmail(e.target.value)}
              className="mt-2 w-full border-b border-carbon/50 bg-transparent px-1 pb-1 font-type text-[17px] text-carbon outline-none focus:border-process"
            />
          </div>
        </div>

        <SectionBand number={2} title="Respondent" />
        <div className="px-4 pb-6 pt-5 sm:px-7">
          <FieldLabel
            htmlFor="respondent"
            number="3"
            label="Party complained of"
            hint="The person, animal, object, or condition responsible. One respondent per form."
          />
          <input
            id="respondent"
            type="text"
            value={respondent}
            maxLength={RESPONDENT_LIMIT}
            autoComplete="off"
            onChange={(e) => setRespondent(e.target.value)}
            className="mt-2 w-full border-b border-carbon/50 bg-transparent px-1 pb-1 font-type text-[17px] text-carbon outline-none focus:border-process"
          />
        </div>

        <SectionBand number={3} title="Nature of the complaint" />
        <div className="px-4 pb-6 pt-5 sm:px-7">
          <FieldLabel
            htmlFor="complaintText"
            number="4"
            label="Statement of grievance"
            hint="Describe what happened, in your own words. Continue on additional sheets if necessary."
          />
          <textarea
            id="complaintText"
            value={complaintText}
            rows={7}
            maxLength={GRIEVANCE_LIMIT}
            onChange={(e) => setComplaintText(e.target.value)}
            className="ruled-field mt-2 w-full resize-y border border-carbon/25 px-2 font-type text-[15px] text-carbon outline-none focus:border-process"
          />
          <p className="mt-1 text-right font-form text-[10px] uppercase tracking-[0.16em] text-carbon/45 tabular-nums">
            {complaintText.length} / {GRIEVANCE_LIMIT} characters
          </p>
        </div>

        <div className="border-t border-carbon/20 px-4 py-5 sm:px-7">
          <p className="font-form text-[11px] leading-relaxed text-carbon/70">
          By filing this form you certify that the statements above are true to the best of your
            knowledge. Filing a knowingly false complaint is a Class II infraction, punishable by
            cat, jam hands, resting hmm face, or any other penalty deemed appropriate by the arbiter.
          </p>

          {notice && (
            <p role="alert" className="mt-4 border-l-4 border-stamp bg-stamp/5 px-3 py-2 font-form text-sm font-semibold text-stamp">
              {notice}
            </p>
          )}

          <button
            type="submit"
            disabled={filing}
            className="mt-5 w-full bg-carbon px-6 py-3.5 font-form text-sm font-bold uppercase tracking-[0.22em] text-file transition-colors hover:bg-process disabled:cursor-not-allowed disabled:bg-carbon/40"
          >
            {filing ? 'Filing…' : 'File this complaint'}
          </button>
        </div>

        <OfficialUseBlock />
      </form>
    </FormSheet>
  )
}

/**
 * What stands in for the form while the window is shut. The department does
 * not apologise; it states the position and quotes an interval, drawn from the
 * same list a receipt draws its review estimate from.
 */
function WindowClosed({ intake }: { intake: Intake }) {
  return (
    <FormSheet>
      <SectionBand title="Notice to complainants" />
      <div className="px-4 pb-6 pt-7 sm:px-7">
        <h2 className="font-display text-[1.35rem] font-semibold uppercase leading-tight tracking-[0.06em] text-carbon sm:text-[1.7rem]">
          The department is not currently accepting complaints.
        </h2>
        <p className="mt-3 font-form text-sm leading-relaxed text-carbon/75">
          The complaint window is closed. No form may be filed and no case number will be issued
          until it reopens.
        </p>

        {intake.reopenEstimate && (
          <p className="mt-5 border-l-4 border-stamp bg-stamp/5 px-3 py-2 font-form text-sm font-semibold text-stamp">
            Check back in {intake.reopenEstimate}.
          </p>
        )}

        <p className="mt-5 font-form text-[11px] leading-relaxed text-carbon/70">
          A grievance arising during the closure is not waived and may be filed when the window
          reopens. Complaints regarding the closure itself cannot be accepted, for the reason
          stated above.
        </p>
      </div>

      <OfficialUseBlock />
    </FormSheet>
  )
}

function FieldLabel({
  htmlFor,
  number,
  label,
  hint,
}: {
  htmlFor: string
  number: string
  label: string
  hint: string
}) {
  return (
    <>
      <label
        htmlFor={htmlFor}
        className="flex items-baseline gap-2 font-form text-[13px] font-bold uppercase tracking-[0.12em] text-carbon"
      >
        <span className="tabular-nums">{number}.</span>
        {label}
      </label>
      <p className="mt-0.5 font-form text-[11px] italic leading-snug text-carbon/60">{hint}</p>
    </>
  )
}
